import {v4 as uuid} from 'uuid';

/**
 * Identity for the current Baseline Builder session: one client-generated id from the moment the
 * builder opens until it reaches a terminal outcome or is closed.
 *
 * Module state rather than React state: the session starts in ProjectCreationChoiceModal and is
 * carried into AiProjectPlanningModal, which share no component above the modal conductor, and the
 * upload region fires events of its own. The thread id is read through a getter registered by the
 * main modal, so an event never carries a thread id that has since been reset.
 *
 * The id is mirrored to sessionStorage so the handoff from the choice modal to the main modal, and
 * a reload of a restored main modal, carry on as the same session.
 */
const STORAGE_KEY = 'baseline_builder_tracking_session';

const noThread = (): null => null;

let builderSessionId: string | null = null;
let threadIdSource: () => string | null | undefined = noThread;

const persist = (): void => {
	try {
		if (builderSessionId) {
			sessionStorage.setItem(STORAGE_KEY, JSON.stringify({builderSessionId}));
		} else {
			sessionStorage.removeItem(STORAGE_KEY);
		}
	} catch {
		// sessionStorage unavailable: the session still works for this page life.
	}
};

const readStored = (): string | null => {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as {builderSessionId?: unknown};
		return typeof parsed?.builderSessionId === 'string' && parsed.builderSessionId ? parsed.builderSessionId : null;
	} catch {
		return null;
	}
};

/** Starts a new session, replacing any live one, and returns its id. */
export const startBuilderSession = (): string => {
	builderSessionId = uuid();
	persist();
	return builderSessionId;
};

/**
 * Returns the live session's id, loading one stored by an earlier modal or page life if none is in
 * memory. Returns null when there is no session to continue; it never starts one.
 */
export const resumeBuilderSession = (): string | null => {
	if (!builderSessionId) builderSessionId = readStored();
	return builderSessionId;
};

export const endBuilderSession = (): void => {
	builderSessionId = null;
	persist();
};

/**
 * The ids every Baseline Builder event carries. Starts a session if none exists, so no event lacks
 * one. A session started here is kept in memory only: it has no Opened event behind it, so a
 * restored modal must not pick it up and join it to an unrelated attempt.
 */
export const getBuilderIdentity = (): {builderSessionId: string; threadId: string | null} => {
	const id = resumeBuilderSession() ?? (builderSessionId = uuid());
	return {builderSessionId: id, threadId: threadIdSource() || null};
};

/**
 * Registers where the live thread id is read from, and returns the unregister for the caller's
 * cleanup. The unregister only clears the source while it is still this one, so a newer
 * registration is never torn down by an older one's cleanup.
 */
export const setBuilderThreadIdSource = (fn: () => string | null | undefined): (() => void) => {
	threadIdSource = fn;
	return () => {
		if (threadIdSource === fn) threadIdSource = noThread;
	};
};

/** Clears module state. `keepStorage` simulates a page reload, which keeps sessionStorage. */
export const resetBuilderSessionForTests = ({keepStorage = false}: {keepStorage?: boolean} = {}): void => {
	builderSessionId = null;
	threadIdSource = noThread;
	if (!keepStorage) {
		try {
			sessionStorage.removeItem(STORAGE_KEY);
		} catch {
			// sessionStorage unavailable
		}
	}
};
